import { WebsocketSignalRService } from 'src/app/shared/services/websocket-signalr.service';
import { WebsocketCommandType } from 'src/app/shared/enums/websocket-command-type.enum';
import { AnalyticsService } from 'src/app/shared/services/analytics/analytics.service';
import { Observable, take, map, BehaviorSubject, of, catchError } from 'rxjs';
import { InventoryService } from 'src/app/shared/services/inventory.service';
import { ToastsService } from 'src/app/shared/services/toasts.service';
import { ChatStateService } from '../chat-state.service';
import { UseGiftResponse } from './use-gift.interface';
import { TranslateService } from '@ngx-translate/core';
import { NavHelper } from 'src/app/shared/helpers';
import { AiChatGift } from './ai-chat-gift.interface';
import { Injectable } from '@angular/core';
import { ChatType } from 'src/app/shared/types/chat-data.interface';
import { TutorialService } from 'src/app/shared/services/communication_services/tutorial.service';

@Injectable({ providedIn: 'root' })
export class ChatGiftsService {
  public readonly gifts$ = new BehaviorSubject<AiChatGift[]>([]);

  constructor(
    private _websocketSignalRService: WebsocketSignalRService,
    private _chatStateService: ChatStateService,
    private _inventoryService: InventoryService,
    private _translateService: TranslateService,
    private _analyticsService: AnalyticsService,
    private _tutorialService: TutorialService,
    private _toastsService: ToastsService,
    private _navHelper: NavHelper,
  ) {}

  public fetchGifts(): Observable<boolean> {
    return this._websocketSignalRService
      .invoke<AiChatGift[]>(WebsocketCommandType.getAiChatGifts)
      .pipe(
        take(1),
        map((res) => {
          this.gifts$.next(res);
          return true;
        }),
      );
  }

  public sendGift(gift: AiChatGift): Observable<boolean> {
    if (this._chatStateService.chatType$.value === ChatType.tutorial) {
      return this.sendTutorialGift(gift);
    }

    if (gift.cost > this._inventoryService.gems) {
      this._navHelper.goToShopGems();
      this._toastsService.showError(
        this._translateService.instant('SHOP_PAGE.NOT_ENOUGH_TOKENS'),
      );

      return of(null);
    }

    return this._websocketSignalRService
      .invoke<UseGiftResponse>(WebsocketCommandType.useAiChatGift, {
        chatId: this._chatStateService.chatId,
        giftId: gift.id,
      })
      .pipe(
        map((res) => {
          this._analyticsService.giftBuy({
            chatId: this._chatStateService.chatId,
            giftName: gift.name,
          });

          this._chatStateService.aiChatRating$.next({
            ...this._chatStateService.aiChatRating$.value,
            rating: res.rating,
          });
          this._inventoryService.setGems(res.gems);

          this._navHelper.goToChat(this._chatStateService.chatId);

          return true;
        }),
        catchError((error) => {
          switch (error.error.status) {
            case 'notEnoughCoins':
              this._navHelper.goToShopGems();
              this._toastsService.showError(
                this._translateService.instant('SHOP_PAGE.NOT_ENOUGH_TOKENS'),
              );
              break;
          }
          console.error(error);
          return of(null);
        }),
      );
  }

  private sendTutorialGift(gift: AiChatGift): Observable<boolean> {
    this._tutorialService.setStep('chat-finish');
    this._navHelper.goToChat(this._chatStateService.chatId);

    this._websocketSignalRService
      .invoke<UseGiftResponse>(WebsocketCommandType.useTutorialGift, {
        chatId: this._chatStateService.chatId,
        giftId: gift.id,
      })
      .subscribe((res) => {
        this._chatStateService.aiChatRating$.next({
          ...this._chatStateService.aiChatRating$.value,
          rating: res.rating,
        });
        this._inventoryService.setGems(res.gems);

        return true;
      });
    return of(true);
  }
}
